import { createContext, FC, useContext, useEffect, useState } from "react";
import { OASContextProps } from "../../../interfaces/SuperAdmin/ContextProps/iOAS";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/Auth";
import { iDataOrganizacion } from "../../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { getAllOrganizations, getOrganizationById } from "../../../api/broker";
import { toast } from "react-toastify";
import { SelectChangeEvent } from "@mui/material";
import { iTipoObjetivo } from "../../../interfaces/SuperAdmin/Others/iDataTipoObjetivos";
import { getAllTiposObjetivo } from "../../../api/broker/tipo_objetivo";

const OASContext = createContext<OASContextProps>({} as OASContextProps);

interface Props {
  children: React.ReactNode;
}

const OASProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();
  const { canAccess } = useAuthContext();

  const [organizaciones, setOrganizaciones] = useState<iDataOrganizacion[]>([]);
  const [organizacion, setOrganizacion] = useState<iDataOrganizacion | null>(
    null
  );
  const [tiposObjetivo, setTiposObjetivo] = useState<iTipoObjetivo[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
    idOrganization
  );

  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingOthers, setIsLoadingOthers] = useState(false);

  const navigate = useNavigate();

  // Fetch de organizaciones para seleccionar
  const fetchOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const orgsRes = await getAllOrganizations();
      console.log(orgsRes);

      if (orgsRes.data.statusCode === 200) {
        setOrganizaciones(orgsRes.data.body);
      } else {
        toast.error("Error al obtener las organizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las organizaciones");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  // Fetch de una organización y los tipos de objetivo
  const fetchOthers = async (id: string) => {
    setIsLoadingOthers(true);
    try {
      const [orgsRes, tiposRes] = await Promise.all([
        getOrganizationById(id),
        getAllTiposObjetivo(),
      ]);

      console.log(orgsRes);

      if (orgsRes.data.statusCode === 200) {
        setOrganizacion(orgsRes.data.body);
      } else {
        toast.error("Error al obtener la organización");
      }

      console.log(tiposRes);

      if (tiposRes.data.statusCode === 200) {
        setTiposObjetivo(tiposRes.data.body);
      } else {
        toast.error("Error al obtener los tipos de objetivo");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener la organización y/o los tipos de objetivo");
    } finally {
      setIsLoadingOthers(false);
    }
  };

  useEffect(() => {
    const isSuperAdmin = canAccess("view:oas");
    if (isSuperAdmin) fetchOrgs();
  }, []);

  const handleSelectChange = async (event: SelectChangeEvent) => {
    setSelectedOrg(event.target.value);
    navigate(`/asistente/oas/${event.target.value}`);
  };

  // Función para traer los tipos de objetivos totales y ver a cuáles tiene acceso la organización
  useEffect(() => {
    if (selectedOrg) {
      fetchOthers(selectedOrg);
    }
  }, [selectedOrg]);

  return (
    <OASContext.Provider
      value={{
        organizaciones,
        organizacion,
        tiposObjetivo,
        selectedOrg,
        isLoadingOrgs,
        isLoadingOthers,
        handleSelectChange,
      }}
    >
      {children}
    </OASContext.Provider>
  );
};

const useOAS = () => useContext(OASContext);

export { OASProvider, useOAS };
